import {
  addPriority,
  deletePriority,
  getAllPriorities,
  updatePriority,
} from "api/siteSettings/prioirty";
import { useMutation, useQuery } from "@tanstack/react-query";
import { isEqual, printError, useSearch, useUtilities } from "helpers";
import { useEffect } from "react";
import {
  addSinglePriority,
  deleteSinglePriority,
  getPriorities,
  updateSinglePriority,
} from "redux/reducers/siteSettings/priorityReducer";
import { ApiResponseType, NotificationType } from "enums";
import { t } from "i18next";

function useGetPriority() {
  const { web = null } = useSearch();
  return useQuery(["priorityList", web], () => getAllPriorities(web));
}

export function usePriority() {
  const { web = null } = useSearch();
  const { data, isLoading, error } = useGetPriority();
  const {
    dispatch,
    priorities: { priorities, loaded, website_id },
  } = useUtilities();

  useEffect(() => {
    if (data && (website_id !== web || !loaded)) {
      dispatch(
        getPriorities({
          priorities: data.data,
          website_id: web,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, loaded, data, web, website_id]);

  return {
    data: priorities,
    isLoading: isLoading || !loaded,
    error,
  };
}

export function useDeletePriority(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deletePriority, {
    onSuccess: (data) => {
      if (isEqual(data.status_code, ApiResponseType.SUCCESS)) {
        dispatch(deleteSinglePriority({ id: data.id }));
        notification({
          type: "success",
          message: t("siteSetting.priority.api.success.priorityDeleted"),
        });
        if (redirect) navigate(redirect);
      } else {
        notification({
          type: "error",
          message: printError(data.message),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: printError(err.message),
      });
    },
  });
}

export function useUpdatePriority() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updatePriority, {
    onSuccess: (data) => {
      if (isEqual(data.status_code, ApiResponseType.SUCCESS)) {
        dispatch(updateSinglePriority(data.data));
        notification({
          type: NotificationType.SUCCESS,
          message: t("siteSetting.priority.api.success.priorityUpdated"),
        });
      } else {
        notification({
          type: NotificationType.ERROR,
          message: printError(data.message),
        });
      }
    },
    onError: (error) => {
      console.log(error);
      notification({
        type: "error",
        message: t("common.error.somethingWentWrongTryLater"),
      });
    },
  });
}

export function useAddPriority(redirect = "") {
  const { web = null } = useSearch();
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(addPriority, {
    onSuccess: (data) => {
      if (!isEqual(data.status_code, ApiResponseType.SUCCESS)) {
        notification({
          type: "error",
          message: printError(data.message),
        });
        return;
      }
      dispatch(addSinglePriority(data.data));
      if (redirect)
        navigate(`${redirect}${data.data.id}${web ? `?web=${web}` : ""}`);
      notification({
        type: "success",
        message: t("siteSetting.priority.api.success.priorityAdded"),
      });
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(error.message),
      });
    },
  });
}
